import { Box, Tooltip } from "@mui/material";
import type { GridApiPremium } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useEffect, useMemo, useState } from "react";

interface Props {
  apiRef: RefObject<GridApiPremium | null>;
}

const ColumnResizeTooltip = ({ apiRef }: Props) => {
  const [resize, setResize] = useState<{ width: number; left: number; top: number }>();

  useEffect(() => {
    const api = apiRef.current;
    if (!api) {
      return;
    }

    let frame: number | undefined;
    let pending: { width: number; element: HTMLElement } | undefined;
    const cancelFrame = () => {
      if (frame !== undefined) {
        cancelAnimationFrame(frame);
        frame = undefined;
      }
      pending = undefined;
    };
    const unsubscribeResize = api.subscribeEvent("columnResize", ({ width, element }) => {
      if (!element) {
        return;
      }
      pending = { width, element };
      if (frame !== undefined) {
        return;
      }
      frame = requestAnimationFrame(() => {
        frame = undefined;
        if (!pending) {
          return;
        }
        const rect = pending.element.getBoundingClientRect();
        setResize({ width: Math.round(pending.width), left: rect.right, top: rect.top });
        pending = undefined;
      });
    });
    const unsubscribeStop = api.subscribeEvent("columnResizeStop", () => {
      cancelFrame();
      setResize(undefined);
    });
    return () => {
      unsubscribeResize();
      unsubscribeStop();
      cancelFrame();
    };
  }, [apiRef]);

  const anchorEl = useMemo(
    () => (resize ? { getBoundingClientRect: () => new DOMRect(resize.left, resize.top, 0, 0) } : undefined),
    [resize]
  );

  return (
    <Tooltip
      open={resize !== undefined}
      title={resize ? `Width: ${resize.width} px` : ""}
      placement="top"
      disableHoverListener
      disableFocusListener
      disableTouchListener
      disableInteractive
      slotProps={{ popper: { anchorEl }, transition: { timeout: 0 } }}
    >
      <Box component="span" />
    </Tooltip>
  );
};

export default ColumnResizeTooltip;
